import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  normalizePlanReviewActionParams,
  resolvePlanReviewCategoryActionParam,
} from 'partner360/lib/planReviewActionParams'

import {
  FinancialCockpitPendingActionOwner,
  type IFinancialCockpitPendingAction,
} from '../../../api/types'
import { useFilteredCockpitPartnerData } from '../../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitOffering,
  selectCockpitPartnerData,
  selectCockpitPendingActions,
  selectCockpitPendingActionsUsers,
} from '../../../model/selectors'
import { PENDING_ACTIONS_UNCATEGORIZED } from '../lib'
import {
  type IPendingActionsCategoryGroup,
  type IPendingActionsUserOption,
  type IPendingActionWithOffering,
  type IUsePendingActionsGroupsResult,
  type PendingActionsOwnerTab,
} from '../types'

const comparePendingActions = (
  a: IFinancialCockpitPendingAction,
  b: IFinancialCockpitPendingAction
) => a.ActionSortOrder - b.ActionSortOrder || a.ActionOrder - b.ActionOrder

export const usePendingActionsGroups = (
  activeTab: PendingActionsOwnerTab | null,
  selectedUserKey: string | null,
  isManagerCockpitMode = false
): IUsePendingActionsGroupsResult => {
  const pendingActions = useSelector(selectCockpitPendingActions)
  const users = useSelector(selectCockpitPendingActionsUsers)
  const partnerData = useSelector(selectCockpitPartnerData)
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const filteredPartnerData = useFilteredCockpitPartnerData()

  return useMemo(() => {
    const partnersInScope = isManagerCockpitMode ? filteredPartnerData : partnerData
    const partnerIdsInScope = new Set(partnersInScope.map((partner) => partner.PartnerId))
    const partnerNamesById = new Map(
      partnersInScope.map((partner) => [partner.PartnerId, partner.PartnerName])
    )
    const normalizeEmail = (email?: string) => {
      if (!email) {
        return ''
      }
      return email.trim().toLowerCase()
    }

    const offeringsInScope = pendingActions.filter(
      ({ PartnerId, Offering }) =>
        (!isManagerCockpitMode || partnerIdsInScope.has(PartnerId)) &&
        (selectedOffering === ProgramTypes.Overall ||
          selectedOffering === ProgramTypes.Consolidated ||
          Offering === selectedOffering)
    )

    const actionsWithCategory = offeringsInScope.flatMap(({ PartnerId, Offering, PBMActions }) =>
      PBMActions.map((action) => ({
        action,
        actionCategory: resolvePlanReviewCategoryActionParam(
          normalizePlanReviewActionParams(action.ActionParam)
        ),
        PartnerId,
        Offering,
      }))
    )

    const allActionsInScope = actionsWithCategory
      .filter(
        ({ actionCategory }) => selectedCategory === null || actionCategory === selectedCategory
      )
      .map<IPendingActionWithOffering>(({ action, PartnerId, Offering }) => ({
        ...action,
        PartnerId,
        Offering: Offering as ProgramTypes,
        PartnerName: partnerNamesById.get(PartnerId),
        User: users.find(
          (user) => normalizeEmail(user.Email) === normalizeEmail(action.ActionUser)
        ),
      }))

    const ownerTabs = allActionsInScope.reduce<
      NonNullable<IUsePendingActionsGroupsResult['ownerTabs']>
    >((tabs, { ActionOwner }) => {
      const tab = tabs.find(({ owner }) => owner === ActionOwner)
      if (tab) {
        tab.count += 1
      } else {
        tabs.push({ owner: ActionOwner, count: 1 })
      }
      return tabs
    }, [])
    ownerTabs.sort((a, b) =>
      a.owner === FinancialCockpitPendingActionOwner.Mine
        ? -1
        : b.owner === FinancialCockpitPendingActionOwner.Mine
          ? 1
          : 0
    )
    const effectiveOwner = ownerTabs.some(({ owner }) => owner === activeTab)
      ? activeTab
      : ownerTabs[0]?.owner
    const ownerActions = effectiveOwner
      ? allActionsInScope.filter(({ ActionOwner }) => ActionOwner === effectiveOwner)
      : allActionsInScope
    const usersByOwner = ownerTabs.reduce<IUsePendingActionsGroupsResult['usersByOwner']>(
      (usersMap, { owner }) => {
        if (owner === FinancialCockpitPendingActionOwner.Mine) {
          usersMap[owner] = []
          return usersMap
        }

        const actionsForOwner = allActionsInScope.filter(({ ActionOwner }) => ActionOwner === owner)
        const optionsByEmail = new Map<string, IPendingActionsUserOption>()

        actionsForOwner.forEach(({ User }) => {
          if (!User) return

          const emailKey = normalizeEmail(User.Email)
          const existing = optionsByEmail.get(emailKey)
          if (existing) {
            existing.actionCount += 1
          } else {
            optionsByEmail.set(emailKey, { ...User, actionCount: 1 })
          }
        })

        usersMap[owner] = Array.from(optionsByEmail.values())
        return usersMap
      },
      {}
    )
    const usersInScope = effectiveOwner ? (usersByOwner[effectiveOwner] ?? []) : []
    const actionsInScope = ownerActions
      .filter(
        ({ User }) =>
          selectedUserKey === null ||
          (User && normalizeEmail(User.Email) === normalizeEmail(selectedUserKey))
      )
      .sort(comparePendingActions)

    const groups = actionsInScope.reduce<IPendingActionsCategoryGroup[]>(
      (categoryGroups, action) => {
        const category = action.ActionCategory || PENDING_ACTIONS_UNCATEGORIZED
        const offering = action.Offering
        let categoryGroup = categoryGroups.find((group) => group.category === category)

        if (!categoryGroup) {
          categoryGroup = { category, offerings: [] }
          categoryGroups.push(categoryGroup)
        }

        let offeringGroup = categoryGroup.offerings.find((group) => group.offering === offering)

        if (!offeringGroup) {
          offeringGroup = { offering, actions: [] }
          categoryGroup.offerings.push(offeringGroup)
        }

        offeringGroup.actions.push(action)
        return categoryGroups
      },
      []
    )

    return {
      groups,
      actions: actionsInScope,
      totalCount: actionsInScope.length,
      ownerTabs,
      usersInScope,
      usersByOwner,
    }
  }, [
    activeTab,
    filteredPartnerData,
    partnerData,
    pendingActions,
    isManagerCockpitMode,
    selectedOffering,
    selectedCategory,
    selectedUserKey,
    users,
  ])
}
